import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BRANDS, SAMPLE_PRODUCTS } from '../data/siteData';
import { Search } from 'lucide-react';

export default function BrandsPage(): React.ReactElement {
  const [search, setSearch] = useState<string>('');

  const filtered = useMemo(() => {
    if (!search) return BRANDS;
    return BRANDS.filter(b => b.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  // Group by first letter
  const grouped = useMemo(() => {
    const groups: Record<string, string[]> = {};
    filtered.forEach(b => {
      const letter = b[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(b);
    });
    return groups;
  }, [filtered]);

  const letters = Object.keys(grouped).sort();

  return (
    <div data-testid="brands-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Tutti i Marchi</h1>
            <p className="text-muted-foreground mt-1">{BRANDS.length} marchi trattati per stampanti, copiatrici e multifunzione</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca marchio..."
              className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))] placeholder:text-muted-foreground"
              data-testid="brands-search-input"
            />
          </div>
        </div>

        {/* Letter Jump */}
        <div className="flex flex-wrap gap-1 mb-6">
          {letters.map(l => (
            <a key={l} href={`#brand-${l}`} className="w-8 h-8 rounded-md border text-sm font-medium flex items-center justify-center hover:bg-[hsl(210,40%,93%)] hover:border-[hsl(var(--dtk-focus))] transition-colors duration-150">{l}</a>
          ))}
        </div>

        {/* Brand Grid */}
        {letters.map(letter => (
          <div key={letter} id={`brand-${letter}`} className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-[hsl(214,58%,41%)]">{letter}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {grouped[letter].map(brand => {
                const productCount = SAMPLE_PRODUCTS.filter(p => p.brand === brand).length;
                return (
                  <Link
                    key={brand}
                    to={`/prodotti/toner?marca=${encodeURIComponent(brand)}`}
                    className="rounded-lg border bg-white px-4 py-3 flex items-center justify-between hover:bg-secondary/40 hover:border-[hsl(var(--dtk-focus))] transition-colors duration-150"
                    data-testid="brand-card"
                  >
                    <span className="text-sm font-medium">{brand}</span>
                    {productCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{productCount} prodotti</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">Nessun marchio trovato per "{search}"</div>
        )}
      </div>
    </div>
  );
}
